// src/pages/SimuladoPage.tsx — elapsed timer (no countdown), relaxed UI

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Clock, CheckCircle, XCircle, ChevronUp } from 'lucide-react';
import { simuladoService } from '../services/simuladoService';
import { questionService } from '../services/questionService';
import { Simulado, SimuladoAnswer } from '../types/simulado.types';
import { Question } from '../types/question.types';
import './SimuladoPage.css';

const normalizeStatementHtml = (html: string): string => {
    if (!html) return '';
    return html.replace(
        /<\/(span|b|strong|em|i|u)>\s*<br\s*\/?>\s*<(span|b|strong|em|i|u)/gi,
        '</$1> <$2'
    );
};

const SimuladoPage: React.FC = () => {
    const { simuladoId } = useParams<{ simuladoId: string }>();
    const navigate = useNavigate();

    const [simulado, setSimulado] = useState<Simulado | null>(null);
    const [questions, setQuestions] = useState<Question[]>([]);
    const [answers, setAnswers] = useState<Map<string, string>>(new Map());
    const [eliminated, setEliminated] = useState<Map<string, Set<string>>>(new Map());
    const [loading, setLoading] = useState(true);
    const [finishing, setFinishing] = useState(false);
    const [showScrollTop, setShowScrollTop] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    // ── Timer: elapsed only — no countdown pressure ──────────
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    const elapsedRef = useRef(0);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        intervalRef.current = setInterval(() => {
            elapsedRef.current += 1;
            setElapsedSeconds(elapsedRef.current);
        }, 1000);
        return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
    }, []);

    const stopTimer = useCallback(() => {
        if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    }, []);

    const questionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

    const loadSimulado = useCallback(async () => {
        try {
            setLoading(true);
            const data = await simuladoService.getSimulado(simuladoId!);
            setSimulado(data);

            const questionsData = await Promise.all(
                data.questionIds.map((id: string) =>
                    questionService.getQuestion(id).then((res: any) => res.question)
                )
            );
            setQuestions(questionsData);

            if (data.status === 'criado') {
                const started = await simuladoService.startSimulado(simuladoId!);
                setSimulado(started);
            } else if (data.status === 'finalizado') {
                stopTimer();
                const map = new Map<string, string>();
                data.answers.forEach((a: any) => { if (a.selectedAlternative) map.set(a.questionId, a.selectedAlternative); });
                setAnswers(map);
            }
        } catch {
            navigate('/questoes?tab=simulados');
        } finally {
            setLoading(false);
        }
    }, [simuladoId, navigate, stopTimer]);

    useEffect(() => { if (simuladoId) loadSimulado(); }, [simuladoId, loadSimulado]);

    const executeFinish = useCallback(async () => {
        setShowConfirmModal(false);
        stopTimer();
        const totalTime = elapsedRef.current;
        try {
            setFinishing(true);
            const answersArray: SimuladoAnswer[] = simulado!.questionIds.map(questionId => ({
                questionId,
                selectedAlternative: (answers.get(questionId) as 'A' | 'B' | 'C' | 'D' | 'E') || null,
                timeSpent: totalTime
            }));
            const result = await simuladoService.finishSimulado(simulado!._id, answersArray);
            setSimulado(result);
        } catch {
            alert('Erro ao finalizar simulado');
        } finally {
            setFinishing(false);
        }
    }, [simulado, answers, stopTimer]);

    const handleFinish = useCallback(() => {
        if (!simulado || finishing || simulado.status === 'finalizado') return;
        setShowConfirmModal(true);
    }, [simulado, finishing]);

    useEffect(() => {
        if (simulado?.status === 'em_andamento') {
            const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
            window.addEventListener('beforeunload', handler);
            return () => window.removeEventListener('beforeunload', handler);
        }
    }, [simulado?.status]);

    useEffect(() => {
        const handleScroll = () => setShowScrollTop(window.scrollY > 500);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleSelectAlternative = (questionId: string, letter: string) => {
        if (simulado?.status === 'finalizado') return;
        setAnswers(prev => { const n = new Map(prev); n.set(questionId, letter); return n; });
    };

    const toggleEliminate = (questionId: string, letter: string) => {
        setEliminated(prev => {
            const m = new Map(prev);
            const s = new Set(m.get(questionId) || []);
            s.has(letter) ? s.delete(letter) : s.add(letter);
            m.set(questionId, s);
            return m;
        });
    };

    const scrollToQuestion = (questionId: string) => {
        const el = questionRefs.current[questionId];
        if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 120, behavior: 'smooth' });
    };

    const formatTime = (s: number) => {
        const h = Math.floor(s / 3600);
        const m = Math.floor((s % 3600) / 60);
        const sec = s % 60;
        if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
        return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
    };

    if (loading) return <div className="simulado-loading"><div className="spinner" /><p>Carregando simulado...</p></div>;
    if (!simulado || !questions.length) return <div className="simulado-error"><p>Simulado não encontrado</p><button onClick={() => navigate('/')}>Voltar</button></div>;

    const isFinished = simulado.status === 'finalizado';
    const answeredCount = answers.size;

    return (
        <div className="simulado-page">

            {/* ── Header ── */}
            <div className="simulado-header">
                <div className="header-logo">GRAIA</div>

                {!isFinished && (
                    <div className="header-center">
                        <div className="header-timer">
                            <Clock size={15} />
                            <span>{formatTime(elapsedSeconds)}</span>
                            <span className="timer-label">decorrido</span>
                        </div>
                        <span className="header-answered">{answeredCount}/{questions.length} respondidas</span>
                    </div>
                )}

                {!isFinished && (
                    <button className="btn-finish-header" onClick={handleFinish} disabled={finishing}>
                        {finishing ? 'Finalizando...' : 'Finalizar Simulado'}
                    </button>
                )}
                {isFinished && (
                    <button className="btn-back-header" onClick={() => {
                        sessionStorage.setItem('questions_initial_tab', 'simulados');
                        window.location.href = '/dashboard';
                    }}>
                        Ir para o Dashboard
                    </button>
                )}
            </div>

            {/* ── Mapa de questões + resultado unificado ── */}
            <div className={`simulado-question-map${isFinished ? ' simulado-question-map--finished' : ''}`}>
                {isFinished && (
                    <div className="qmap-finish-info">
                        <div className="qmap-finish-title">
                            <CheckCircle size={18} />
                            <span>Simulado concluído — {(simulado as any).name || 'Simulado'}</span>
                        </div>
                        <div className="qmap-finish-stats">
                            <div className="qmap-stat">
                                <span className="qmap-stat-value qmap-stat--ok">{simulado.results.totalCorrect}</span>
                                <span className="qmap-stat-label">Acertos</span>
                            </div>
                            <div className="qmap-stat">
                                <span className="qmap-stat-value qmap-stat--err">{simulado.results.totalWrong}</span>
                                <span className="qmap-stat-label">Erros</span>
                            </div>
                            <div className="qmap-stat">
                                <span className="qmap-stat-value qmap-stat--acc">{simulado.results.accuracy}%</span>
                                <span className="qmap-stat-label">Aproveitamento</span>
                            </div>
                            <div className="qmap-stat">
                                <span className="qmap-stat-value">{formatTime(simulado.results.totalTime)}</span>
                                <span className="qmap-stat-label">Tempo total</span>
                            </div>
                        </div>
                    </div>
                )}
                <div className="question-map-inner">
                    {questions.map((question, index) => {
                        const answered = answers.has(question._id);
                        const savedAnswer = isFinished ? simulado.answers.find((a: any) => a.questionId === question._id) : null;
                        const isCorrect = savedAnswer?.isCorrect;
                        return (
                            <button
                                key={question._id}
                                className={`nav-circle ${answered && !isFinished ? 'answered' : ''} ${isFinished ? (isCorrect ? 'correct' : 'wrong') : ''}`}
                                onClick={() => scrollToQuestion(question._id)}
                                title={`Questão ${index + 1}`}
                            >
                                {index + 1}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* ── Lista de questões ── */}
            <div className="questions-list">
                {questions.map((question, index) => {
                    const userAnswer = answers.get(question._id);
                    const savedAnswer = simulado.answers.find((a: any) => a.questionId === question._id);
                    const isCorrect = savedAnswer?.isCorrect;
                    const eliminatedSet = eliminated.get(question._id) || new Set<string>();
                    return (
                        <div
                            key={question._id}
                            ref={el => { questionRefs.current[question._id] = el; }}
                            className={`question-block ${isFinished ? (isCorrect ? 'correct' : 'wrong') : ''}`}
                        >
                            <div className="question-number">
                                <div className="question-number-left">
                                    <span className="question-num-badge">{index + 1}</span>
                                    {isFinished && (
                                        <span className={`status-badge ${isCorrect ? 'correct' : 'wrong'}`}>
                                            {isCorrect ? <><CheckCircle size={14} /> Correta</> : <><XCircle size={14} /> Errada</>}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {question.statementImage ? (
                                <div className="question-statement">
                                    <div dangerouslySetInnerHTML={{ __html: normalizeStatementHtml(question.statement) }} />
                                    <img src={question.statementImage} alt="Imagem da questão" />
                                </div>
                            ) : (
                                <div className="question-statement" dangerouslySetInnerHTML={{ __html: normalizeStatementHtml(question.statement) }} />
                            )}

                            <div className="alternatives-list">
                                {question.alternatives.map(alt => {
                                    const isSelected = userAnswer === alt.letter;
                                    const isCorrectAlt = isFinished && question.correctAlternative === alt.letter;
                                    const isEliminated = !isFinished && eliminatedSet.has(alt.letter);
                                    return (
                                        <div
                                            key={alt.letter}
                                            role="button"
                                            tabIndex={isFinished || isEliminated ? -1 : 0}
                                            className={`alternative-item ${isSelected ? 'selected' : ''} ${isEliminated ? 'eliminated' : ''} ${isFinished ? 'finished' : ''} ${isFinished && isCorrectAlt ? 'correct-answer' : ''} ${isFinished && isSelected && !isCorrectAlt ? 'wrong-answer' : ''}`}
                                            onClick={() => { if (!isEliminated && !isFinished) handleSelectAlternative(question._id, alt.letter); }}
                                            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (!isEliminated && !isFinished) handleSelectAlternative(question._id, alt.letter); } }}
                                        >
                                            <span className="alt-letter">{alt.letter}</span>
                                            <span className="alt-text" dangerouslySetInnerHTML={{ __html: alt.text }} />
                                            {!isFinished && (
                                                <button
                                                    className="btn-eliminate"
                                                    onClick={e => { e.stopPropagation(); toggleEliminate(question._id, alt.letter); }}
                                                    title={isEliminated ? 'Restaurar alternativa' : 'Eliminar alternativa'}
                                                    tabIndex={-1}
                                                >
                                                    {isEliminated ? '↩' : '✕'}
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {isFinished && (
                                <div className="question-resolution">
                                    {!isCorrect && (
                                        <div className="user-answer-info">
                                            <span>Sua resposta: <strong>{userAnswer || 'Não respondida'}</strong></span>
                                            <span>Correta: <strong>{question.correctAlternative}</strong></span>
                                        </div>
                                    )}
                                    {question.explanationText && (
                                        <div className="resolution-text" dangerouslySetInnerHTML={{ __html: question.explanationText }} />
                                    )}
                                    {question.explanationVideoUrl && (
                                        <div className="resolution-buttons">
                                            <a className="btn-resolution active" href={question.explanationVideoUrl} target="_blank" rel="noreferrer">
                                                Ver resolução em vídeo
                                            </a>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}

                {!isFinished && (
                    <div className="simulado-end-actions">
                        <p className="end-reminder">
                            {answeredCount < questions.length
                                ? `Você ainda tem ${questions.length - answeredCount} questão(ões) sem resposta.`
                                : 'Todas as questões foram respondidas.'
                            }
                        </p>
                        <button className="btn-finish-end" onClick={handleFinish} disabled={finishing}>
                            {finishing ? 'Finalizando...' : 'Finalizar Simulado'}
                        </button>
                    </div>
                )}
            </div>

            {showScrollTop && (
                <button className="btn-scroll-top" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                    <ChevronUp size={24} />
                </button>
            )}

            {showConfirmModal && (
                <div className="confirm-modal-overlay" onClick={() => setShowConfirmModal(false)}>
                    <div className="confirm-modal" onClick={e => e.stopPropagation()}>
                        <h3>Finalizar Simulado</h3>
                        <p>
                            {answeredCount < questions.length
                                ? `Você ainda tem ${questions.length - answeredCount} questão(ões) sem resposta. Tem certeza que deseja finalizar?`
                                : 'Tem certeza que deseja finalizar? Você não poderá alterar suas respostas.'
                            }
                        </p>
                        <div className="confirm-modal-actions">
                            <button className="confirm-modal-cancel" onClick={() => setShowConfirmModal(false)}>Continuar</button>
                            <button className="confirm-modal-ok" onClick={executeFinish}>Finalizar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SimuladoPage;
